import type { RouteObject } from "react-router";
import { Navigate } from "react-router";

import { RequirePermission } from "@/auth/RequirePermission";

import { pageHref } from "./registry";
import type { ServiceModule, ServicePage } from "./types";

/**
 * 把一份服務模組宣告轉成 React Router 的路由。
 *
 * 產出的形狀：
 *
 *     /master            → RequirePermission service="master"
 *       index          → 轉址到第一個面向（沒有面向把 path 留空時）
 *       plant          → RequirePermission ["master.view_plant"] → 廠區
 *       materials      → RequirePermission ["master.view_materialcategory"] → 料號
 *       vessels        → RequirePermission ["master.view_vessel"] → 運船
 *       land-locations → RequirePermission ["master.view_landlocation"] → 地點
 *
 * 權限守衛刻意包在 `lazy` 回傳的 Component 外面，而不是用「守衛當 layout
 * route、頁面當 children」那種寫法 —— index route 不能有 children，
 * 那樣的話服務首頁就沒辦法有自己的權限要求，會變成一個只對某些頁面成立的
 * 特例。包在元件外層對每一種路由都一致。
 */
function guardedLazy(page: ServicePage): NonNullable<RouteObject["lazy"]> {
  const { permissions } = page;

  return async () => {
    const { Component } = await page.load();

    if (!permissions?.length) {
      return { Component };
    }

    return {
      Component: function GuardedPage() {
        return (
          <RequirePermission permissions={permissions}>
            <Component />
          </RequirePermission>
        );
      },
    };
  };
}

export function buildServiceRoutes(module: ServiceModule): RouteObject {
  const hasIndexPage = module.pages.some((page) => page.path === "");
  const firstPage = module.pages[0];

  return {
    path: module.code,
    // 服務維度：不是這個服務的成員就整個服務都進不去
    element: <RequirePermission service={module.code} />,
    children: [
      // 沒有面向把 path 留空 —— 也就是這個服務所有面向都有自己的網址
      // 時，服務根路徑本身不是內容，轉址到宣告順序中的第一個面向，
      // 這樣「/master」也能到得了畫面，而不是空白頁或 404。
      ...(!hasIndexPage && firstPage
        ? [{ index: true, element: <Navigate to={pageHref(module.code, firstPage.path)} replace /> }]
        : []),
      ...module.pages.map((page) =>
        page.path === ""
          ? { index: true, lazy: guardedLazy(page) }
          : { path: page.matchNested ? `${page.path}/*` : page.path, lazy: guardedLazy(page) },
      ),
    ],
  };
}
